import { useCallback, useEffect, useMemo, useState } from 'react'

import { useBranch } from '@/contexts/BranchContext'
import { fetchPosProducts } from '@/lib/api/products'

/**
 * @param {import('@/types/api').Product[]} products
 */
export function filterActiveProducts(products) {
  return products.filter((product) => product.is_active)
}

/**
 * @param {object} [options]
 * @param {number | null} [options.branchId]
 * @param {boolean} [options.enabled]
 */
export function usePosProducts({ branchId, enabled = true } = {}) {
  const { activeBranchId } = useBranch()
  const resolvedBranchId = branchId ?? activeBranchId
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const visibleProducts = useMemo(
    () => filterActiveProducts(products),
    [products],
  )

  const reload = useCallback(async () => {
    if (!enabled || !resolvedBranchId) {
      setProducts([])
      setLoading(false)
      return []
    }

    setLoading(true)
    setError('')
    try {
      const items = await fetchPosProducts()
      setProducts(items)
      return items
    } catch (err) {
      setProducts([])
      setError(err?.response?.data?.message ?? 'Unable to load retail products.')
      return []
    } finally {
      setLoading(false)
    }
  }, [resolvedBranchId, enabled])

  useEffect(() => {
    reload()
  }, [reload])

  return {
    products: visibleProducts,
    loading,
    error,
    reload,
  }
}
